import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useOperatorConfig } from './useOperatorConfig';
import { resolveBackendPath } from '../utils/backendUrl';
import type { Capability, SessionData } from '../types';

type AuthSession = SessionData;

interface LoginSuccess {
  success: true;
}

interface LoginFailure {
  success: false;
  message: string;
}

export type LoginResult = LoginSuccess | LoginFailure;

interface AuthContextValue {
  isAuthenticated: boolean;
  isReady: boolean;
  isLoading: boolean;
  session: AuthSession | null;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  hasCapability: (capability: Capability) => boolean;
}

const SESSION_STORAGE_KEY = 'operator-console/session';

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function readStoredSession(): AuthSession | null {
  if (!isBrowser()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<AuthSession>;
    if (
      typeof parsed?.sessionId === 'string' &&
      parsed.user &&
      typeof parsed.user.username === 'string' &&
      Array.isArray(parsed.capabilities)
    ) {
      return {
        ...parsed,
        token: typeof parsed.token === 'string' ? parsed.token : null,
      } as AuthSession;
    }
  } catch (error) {
    console.warn('Failed to read stored session', error);
  }

  return null;
}

function storeSession(session: AuthSession | null) {
  if (!isBrowser()) {
    return;
  }
  if (!session) {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const {
    config: { backendUrl },
  } = useOperatorConfig();

  const [session, setSession] = useState<AuthSession | null>(() => readStoredSession());
  const [isReady, setIsReady] = useState(() => session === null);
  const [isLoading, setIsLoading] = useState(false);

  const resolveApi = useCallback(
    (path: string) => resolveBackendPath(path, backendUrl),
    [backendUrl],
  );

  useEffect(() => {
    storeSession(session);
  }, [session]);

  useEffect(() => {
    let cancelled = false;

    async function validateSession() {
      if (!session) {
        setIsReady(true);
        return;
      }

      try {
        const response = await fetch(resolveApi('/api/admin/session'), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session.token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Session invalid');
        }

        const data = (await response.json()) as SessionData;
        if (!cancelled) {
          const merged: AuthSession = {
            ...data,
            token: data.token ?? session?.token ?? null,
          };
          setSession(merged);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Session validation failed', error);
          setSession(null);
        }
      } finally {
        if (!cancelled) {
          setIsReady(true);
        }
      }
    }

    if (session) {
      setIsReady(false);
    }
    void validateSession();

    return () => {
      cancelled = true;
    };
  }, [session?.token, resolveApi]);

  const login = useCallback(
    async (username: string, password: string): Promise<LoginResult> => {
      setIsLoading(true);
      try {
        const response = await fetch(resolveApi('/api/admin/login'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username, password }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            return { success: false, message: 'Invalid username or password.' };
          }
          if (response.status === 403) {
            return { success: false, message: 'Account disabled. Contact an administrator.' };
          }
          const detail = await response.text();
          return {
            success: false,
            message: detail || 'Unable to sign in. Please try again.',
          };
        }

        const data = (await response.json()) as SessionData;
        setSession({
          ...data,
          token: data.token ?? null,
        });
        return { success: true };
      } catch (error) {
        console.error('Login failed', error);
        return {
          success: false,
          message: 'Unable to reach the backend. Check your connection and try again.',
        };
      } finally {
        setIsLoading(false);
      }
    },
    [resolveApi],
  );

  const logout = useCallback(async () => {
    if (!session) {
      setSession(null);
      return;
    }

    try {
      const headers: HeadersInit | undefined = session.token
        ? {
            Authorization: `Bearer ${session.token}`,
          }
        : undefined;
      await fetch(resolveApi('/api/admin/logout'), {
        method: 'POST',
        headers,
      });
    } catch (error) {
      console.warn('Logout request failed', error);
    } finally {
      setSession(null);
    }
  }, [resolveApi, session]);

  const hasCapability = useCallback(
    (capability: Capability) => session?.capabilities?.includes(capability) ?? false,
    [session?.capabilities],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(session),
      isReady,
      isLoading,
      session,
      login,
      logout,
      hasCapability,
    }),
    [hasCapability, isLoading, isReady, login, logout, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
